import axios from 'axios';
import {
  YahooLeague,
  YahooTeam,
  ApiResponse,
  League,
  Roster,
  Matchup,
  User
} from '../types/fantasy';

// Yahoo Fantasy API - using proxy to avoid CORS issues
const getProxyUrl = () => {
  // Use relative URL for production, localhost for development
  if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
    return 'http://localhost:3000/api/yahoo-proxy';
  }
  return '/api/yahoo-proxy';
};

export class YahooAPI {
  private accessToken: string | null = null;

  constructor(accessToken?: string) {
    this.accessToken = accessToken || null;
  }

  setAccessToken(token: string) {
    this.accessToken = token;
  }

  private async makeRequest<T>(endpoint: string): Promise<ApiResponse<T>> {
    if (!this.accessToken) {
      return {
        success: false,
        error: 'Yahoo API requires OAuth authentication. Please provide access token.',
        platform: 'yahoo'
      };
    }

    try {
      // Remove leading slash from endpoint if present
      const cleanEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;
      
      // Use our proxy endpoint with path as query parameter
      const proxyUrl = `${getProxyUrl()}?path=${encodeURIComponent(cleanEndpoint)}`;
      
      const response = await axios.get(proxyUrl, {
        headers: {
          'Authorization': `Bearer ${this.accessToken}`,
          'Accept': 'application/json'
        }
      });
      
      return {
        success: true,
        data: response.data,
        platform: 'yahoo'
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred',
        platform: 'yahoo'
      };
    }
  }

  async getLeague(leagueKey: string): Promise<ApiResponse<League>> {
    // Yahoo league key format: {game_key}.l.{league_id}
    // Example: 414.l.123456 (414 is NFL 2024 game key)
    const response = await this.makeRequest<any>(`league/${leagueKey}`);
    
    if (!response.success || !response.data) {
      return {
        success: false,
        error: response.error || 'Failed to load league data',
        platform: 'yahoo'
      };
    }

    // Handle Yahoo's JSON response format
    let yahooLeague: any;
    
    // Check different possible response structures
    if (response.data.fantasy_content?.league) {
      // Standard JSON format
      if (Array.isArray(response.data.fantasy_content.league)) {
        yahooLeague = response.data.fantasy_content.league[0];
      } else {
        yahooLeague = response.data.fantasy_content.league;
      }
    }
    
    if (!yahooLeague) {
      // League data structure error
      return {
        success: false,
        error: 'Invalid league data format',
        platform: 'yahoo'
      };
    }

    // Parse league data - handle both array and direct property access
    const league: League = {
      id: Array.isArray(yahooLeague.league_id) ? yahooLeague.league_id[0] : yahooLeague.league_id || leagueKey,
      name: Array.isArray(yahooLeague.name) ? yahooLeague.name[0] : yahooLeague.name || 'Unknown League',
      platform: 'yahoo',
      totalTeams: parseInt(Array.isArray(yahooLeague.num_teams) ? yahooLeague.num_teams[0] : yahooLeague.num_teams) || 10,
      currentWeek: parseInt(Array.isArray(yahooLeague.current_week) ? yahooLeague.current_week[0] : yahooLeague.current_week) || 1,
      season: parseInt(Array.isArray(yahooLeague.season) ? yahooLeague.season[0] : yahooLeague.season) || 2025,
      settings: {
        scoringType: Array.isArray(yahooLeague.scoring_type) ? yahooLeague.scoring_type[0] : yahooLeague.scoring_type || 'head',
        playoffWeeks: undefined
      }
    };

    return {
      success: true,
      data: league,
      platform: 'yahoo'
    };
  }

  async getLeagueTeams(leagueKey: string): Promise<ApiResponse<User[]>> {
    const response = await this.makeRequest<any>(`league/${leagueKey}/teams`);
    
    if (!response.success || !response.data) {
      return {
        success: false,
        error: response.error || 'Failed to load users data',
        platform: 'yahoo'
      };
    }

    // Find teams in the response structure
    let teamsData: any;
    if (response.data.fantasy_content?.league) {
      const league = response.data.fantasy_content.league;
      // Teams might be in different locations
      if (Array.isArray(league) && league[1]?.teams) {
        teamsData = league[1].teams;
      } else if (league.teams) {
        teamsData = league.teams;
      }
    }
    
    if (!teamsData) {
      // Teams data structure error
      return {
        success: false,
        error: 'No teams data available',
        platform: 'yahoo'
      };
    }

    // Parse teams - handle different formats
    const users: User[] = [];
    
    // Handle object with numeric keys
    if (typeof teamsData === 'object' && !Array.isArray(teamsData)) {
      Object.keys(teamsData).forEach(key => {
        if (key !== 'count' && teamsData[key]?.team) {
          const team = teamsData[key].team;
          const teamData = Array.isArray(team) ? team[0] : team;
          users.push({
            userId: (Array.isArray(teamData.team_id) ? teamData.team_id[0] : teamData.team_id) || key,
            displayName: (Array.isArray(teamData.name) ? teamData.name[0] : teamData.name) || 'Team ' + key,
            teamName: (Array.isArray(teamData.name) ? teamData.name[0] : teamData.name) || 'Team ' + key
          });
        }
      });
    }
    // Handle array format
    else if (Array.isArray(teamsData)) {
      teamsData.forEach((teamWrapper: any) => {
        if (teamWrapper.team) {
          const team = Array.isArray(teamWrapper.team) ? teamWrapper.team[0] : teamWrapper.team;
          users.push({
            userId: (Array.isArray(team.team_id) ? team.team_id[0] : team.team_id) || '',
            displayName: (Array.isArray(team.name) ? team.name[0] : team.name) || 'Unknown Team',
            teamName: (Array.isArray(team.name) ? team.name[0] : team.name) || 'Unknown Team'
          });
        }
      });
    }

    return {
      success: true,
      data: users,
      platform: 'yahoo'
    };
  }

  async getTeamRoster(leagueKey: string, teamKey: string, week?: number): Promise<ApiResponse<Roster>> {
    let endpoint = `/team/${leagueKey}.t.${teamKey}/roster`;
    if (week) {
      endpoint += `;week=${week}`;
    }
    
    const response = await this.makeRequest<any>(endpoint);
    
    if (!response.success || !response.data) {
      return {
        success: false,
        error: response.error || 'Failed to load roster data',
        platform: 'yahoo'
      };
    }

    // Parse Yahoo's complex nested structure
    const rosterData = response.data.fantasy_content?.team?.[1]?.roster;
    if (!rosterData) {
      return {
        success: false,
        error: 'No roster data available',
        platform: 'yahoo'
      };
    }

    const players = Object.values(rosterData[0].players)
      .filter((player: any) => player.player)
      .map((playerData: any) => {
        const player = playerData.player[0];
        return {
          id: player.player_id[0],
          name: player.name[0].full[0],
          position: player.display_position[0],
          team: player.editorial_team_abbr?.[0] || 'UNK'
        };
      });

    const roster: Roster = {
      userId: teamKey,
      players,
      starters: [], // Would need to parse selected_position data
      bench: [],   // Would need to parse selected_position data
    };

    return {
      success: true,
      data: roster,
      platform: 'yahoo'
    };
  }

  async getLeagueRosters(leagueKey: string, week?: number): Promise<ApiResponse<Roster[]>> {
    // Try different endpoints to get roster data
    // Start with the most detailed endpoint
    let endpoint = `league/${leagueKey}/teams/roster`;
    let response = await this.makeRequest<any>(endpoint);
    
    // If that fails, try with players subresource
    if (!response.success || !response.data) {
      endpoint = `league/${leagueKey}/teams;out=roster`;
      response = await this.makeRequest<any>(endpoint);
    }
    
    // If that also fails, try just teams endpoint
    if (!response.success || !response.data) {
      endpoint = `league/${leagueKey}/teams`;
      response = await this.makeRequest<any>(endpoint);
    }
    
    if (!response.success || !response.data) {
      return {
        success: false,
        error: response.error || 'Failed to load roster data',
        platform: 'yahoo'
      };
    }

    const rosters: Roster[] = [];

    // Store in window for debugging
    if (typeof window !== 'undefined') {
      (window as any).yahooRosterResponse = response.data;
    }
    
    // Check structure
    const structure = {
      hasFantasyContent: !!response.data?.fantasy_content,
      hasLeague: !!response.data?.fantasy_content?.league,
      leagueIsArray: Array.isArray(response.data?.fantasy_content?.league),
      hasTeams: !!response.data?.fantasy_content?.league?.[1]?.teams,
      teamCount: response.data?.fantasy_content?.league?.[1]?.teams?.count
    };
    
    try {
      // Find teams data in response - Yahoo's structure varies
      let teamsData: any;
      if (response.data.fantasy_content?.league) {
        const league = response.data.fantasy_content.league;
        if (Array.isArray(league)) {
          // Look for teams in array elements
          teamsData = league[1]?.teams || 
                     league.find(item => item?.teams)?.teams;
        } else if (typeof league === 'object') {
          // Direct teams property or in numbered keys
          teamsData = league.teams || 
                     league['1']?.teams ||
                     league['0']?.teams;
        }
      }
      
      // If still no teams, try direct access
      if (!teamsData && response.data.fantasy_content?.teams) {
        teamsData = response.data.fantasy_content.teams;
      }
      
      if (teamsData && typeof teamsData === 'object') {
        // Parse each team's roster
        Object.keys(teamsData).forEach(key => {
          if (key !== 'count' && teamsData[key]) {
            // Yahoo structure has team data nested
            const teamData = teamsData[key];
            
            // Team data is in .team property which is an array
            if (teamData.team && Array.isArray(teamData.team)) {
              
              const team = teamData.team[0]; // Team info
              const rosterData = teamData.team[1]?.roster; // Roster is in second element
              
              if (!team) {
                return;
              }
              
              // Team data is an array - Yahoo returns data as array of mixed values
              // team[0] = {team_key: ...}, team[2] = {name: ...}, etc.
              let teamId = key;
              let teamName = `Team ${key}`;
              
              // Extract team ID and name from the array
              if (Array.isArray(team)) {
                for (let i = 0; i < team.length; i++) {
                  if (team[i] && typeof team[i] === 'object') {
                    if (team[i].team_key) teamId = team[i].team_key;
                    if (team[i].team_id) teamId = team[i].team_id;
                    if (team[i].name) teamName = team[i].name;
                  }
                }
              } else if (team && typeof team === 'object') {
                teamId = team.team_id || team.team_key || key;
                teamName = team.name || `Team ${key}`;
              }
              
              const players: any[] = [];
              const starters: any[] = [];
              const bench: any[] = [];
              
              if (rosterData) {
                // Handle different roster data structures
                let playersData: any = null;
                
                // First check roster['0'].players (Yahoo's common format)
                if (rosterData.roster?.['0']?.players) {
                  playersData = rosterData.roster['0'].players;
                }
                // Then check direct roster.players
                else if (rosterData.roster?.players) {
                  playersData = rosterData.roster.players;
                } 
                // Check if rosterData has direct players property
                else if (rosterData?.players) {
                  playersData = rosterData.players;
                }
                // Handle array format
                else if (Array.isArray(rosterData)) {
                  // Check each array element for roster/players
                  for (let i = 0; i < rosterData.length; i++) {
                    if (rosterData[i]?.roster?.players) {
                      playersData = rosterData[i].roster.players;
                      break;
                    } else if (rosterData[i]?.players) {
                      playersData = rosterData[i].players;
                      break;
                    }
                  }
                } 
                // Handle object with numeric keys (Yahoo sometimes returns array-like objects)
                else if (typeof rosterData === 'object') {
                  const keys = Object.keys(rosterData);
                  
                  // Check each numeric key for roster/players
                  for (const key of keys) {
                    if (!isNaN(Number(key))) {
                      const item = rosterData[key];
                      if (item?.roster?.players) {
                        playersData = item.roster.players;
                        break;
                      } else if (item?.players) {
                        playersData = item.players;
                        break;
                      }
                    }
                  }
                  
                  // Also check for 'roster' key directly
                  if (!playersData && rosterData['roster']) {
                    if (rosterData['roster'].players) {
                      playersData = rosterData['roster'].players;
                    } else if (typeof rosterData['roster'] === 'object') {
                      // Check if roster itself has numeric keys
                      const rosterKeys = Object.keys(rosterData['roster']);
                      for (const rKey of rosterKeys) {
                        if (!isNaN(Number(rKey)) && rosterData['roster'][rKey]?.players) {
                          playersData = rosterData['roster'][rKey].players;
                          break;
                        }
                      }
                    }
                  }
                }
              
              if (playersData && typeof playersData === 'object') {
                const playerKeys = Object.keys(playersData).filter(k => k !== 'count');
                
                playerKeys.forEach(pKey => {
                  if (pKey !== 'count' && playersData[pKey]) {
                    const playerWrapper = playersData[pKey];
                    
                    // Yahoo format: {player: [[...player data...], {selected_position: [...]}]}
                    let playerEntry = playerWrapper.player || playerWrapper;
                    
                    // Yahoo often returns player data as an array
                    let playerData = playerEntry;
                    let playerName = 'Unknown Player';
                    let playerId = pKey;
                    let position = 'UNK';
                    let team = 'UNK';
                    let selectedPos = 'BN';
                    
                    // Debug the exact structure
                    if (playerWrapper.player) {
                      if (Array.isArray(playerWrapper.player)) {
                        if (playerWrapper.player[0]) {
                          if (Array.isArray(playerWrapper.player[0]) && playerWrapper.player[0].length > 0) {
                            // Check first few elements
                          }
                        }
                      }
                      playerEntry = playerWrapper.player;
                    }
                    
                    // If player is an array (common Yahoo format)
                    if (Array.isArray(playerEntry)) {
                      
                      // Yahoo format: [player_data_array, selected_position_object]
                      if (playerEntry[0] && Array.isArray(playerEntry[0])) {
                        // First element is array of player properties
                        const playerDataArray = playerEntry[0];
                        
                        // Loop through player data array to extract properties
                        playerDataArray.forEach((prop, idx) => {
                          // Skip empty arrays that Yahoo includes
                          if (Array.isArray(prop) && prop.length === 0) {
                            return;
                          }
                          
                          if (prop && typeof prop === 'object' && !Array.isArray(prop)) {
                            
                            // Each property is its own object
                            if (prop.player_key) {
                              playerId = prop.player_key;
                            }
                            if (prop.player_id) {
                              playerId = prop.player_id; 
                            }
                            if (prop.name && typeof prop.name === 'object') {
                              if (prop.name.full) {
                                playerName = prop.name.full;
                              }
                            }
                            if (prop.display_position) {
                              position = prop.display_position;
                            }
                            if (prop.editorial_team_abbr) {
                              team = prop.editorial_team_abbr;
                            }
                          }
                        });
                        
                        // Second element has selected position
                        if (playerEntry[1]?.selected_position) {
                          const selPos = playerEntry[1].selected_position;
                          if (Array.isArray(selPos)) {
                            // Find position in the array
                            const posObj = selPos.find((s: any) => s.position);
                            if (posObj) selectedPos = posObj.position;
                          } else if (selPos.position) {
                            selectedPos = selPos.position;
                          }
                        }
                      } else {
                        // Fallback: Loop through array elements
                        playerEntry.forEach((element, idx) => {
                          if (!element) return;
                          
                          if (element?.player_key) playerId = element.player_key;
                          if (element?.player_id) playerId = element.player_id;
                          if (element?.name?.full) playerName = element.name.full;
                          if (element?.display_position) position = element.display_position;
                          if (element?.editorial_team_abbr) team = element.editorial_team_abbr;
                          
                          if (element?.selected_position) {
                            if (Array.isArray(element.selected_position)) {
                              const posObj = element.selected_position.find((s: any) => s?.position);
                              if (posObj) selectedPos = posObj.position;
                            } else if (element.selected_position?.position) {
                              selectedPos = element.selected_position.position;
                            }
                          }
                        });
                      }
                    } else if (typeof playerEntry === 'object') {
                      // Handle object format
                      playerData = playerEntry;
                      
                      if (playerData.player_key) playerId = playerData.player_key;
                      if (playerData.player_id) playerId = playerData.player_id;
                      
                      if (playerData.name?.full) {
                        playerName = playerData.name.full;
                      } else if (Array.isArray(playerData.name) && playerData.name[0]?.full) {
                        playerName = playerData.name[0].full;
                      }
                      
                      position = playerData.display_position || playerData.position_type || 'UNK';
                      team = playerData.editorial_team_abbr || 'UNK';
                      
                      if (playerData.selected_position) {
                        if (typeof playerData.selected_position === 'object' && playerData.selected_position.position) {
                          selectedPos = playerData.selected_position.position;
                        } else if (Array.isArray(playerData.selected_position) && playerData.selected_position[0]?.position) {
                          selectedPos = playerData.selected_position[0].position;
                        }
                      }
                    } else {
                      return;
                    }
                    
                    // Create player info object
                    const playerIdStr = String(playerId);
                    const playerInfo = {
                      id: playerIdStr,
                      name: playerName || 'Unknown Player',
                      position: position || 'UNK',
                      team: team || 'UNK'
                    };
                    
                    players.push(playerInfo);
                    
                    // Check if starter (BN = bench, any other position = starter)
                    
                    if (selectedPos !== 'BN' && selectedPos !== 'IR' && selectedPos !== 'bench') {
                      starters.push(playerInfo);
                    } else {
                      bench.push(playerInfo);
                    }
                  }
                });
              }
            }
              
              // Convert starter and bench arrays to IDs
              const starterIds = starters.map(p => p.id);
              const benchIds = bench.map(p => p.id);
              
              if (players.length > 0) {
        rosters.push({
                  userId: teamId,
                  teamName,
                  players,
                  starters: starterIds,
                  bench: benchIds,
                  totalPoints: 0
                });
              }
            }
          }
        });
      } else {
        // No teams data found in response
        // Try to get basic team info
        const teamsResponse = await this.getLeagueTeams(leagueKey);
        if (teamsResponse.success && teamsResponse.data) {
          return {
            success: true,
            data: teamsResponse.data.map(user => ({
              userId: user.userId,
              teamName: user.displayName,
              players: [],
              starters: [],
              bench: [],
              totalPoints: 0
            })),
            platform: 'yahoo'
          };
        }
      }
    } catch (error) {
      // Return basic roster structure even if parsing fails
      const teamsResponse = await this.getLeagueTeams(leagueKey);
      if (teamsResponse.success && teamsResponse.data) {
        return {
          success: true,
          data: teamsResponse.data.map(user => ({
            userId: user.userId,
            teamName: user.displayName,
            players: [],
            starters: [],
            bench: [],
            totalPoints: 0
          })),
          platform: 'yahoo'
        };
      }
    }

    return {
      success: true,
      data: rosters,
      platform: 'yahoo'
    };
  }

  async getMatchups(leagueKey: string, week: number): Promise<ApiResponse<Matchup[]>> {
    // For current week, don't specify week parameter
    // Yahoo uses different format for current vs past weeks
    let response: ApiResponse<any>;
    
    if (week === 1 || !week) {
      // Try without week parameter for current/week 1
      response = await this.makeRequest<any>(`league/${leagueKey}/scoreboard`);
    } else {
      // Try with week parameter for specific weeks
      response = await this.makeRequest<any>(`league/${leagueKey}/scoreboard;week=${week}`);
    }
    
    // If that fails, try matchups endpoint
    if (!response.success || !response.data) {
      response = await this.makeRequest<any>(`league/${leagueKey}/matchups;weeks=${week}`);
    }
    
    if (!response.success || !response.data) {
      return {
        success: false,
        error: response.error || 'Failed to load matchup data',
        platform: 'yahoo'
      };
    }

    const matchups: Matchup[] = [];
    
    // Store in window for debugging
    if (typeof window !== 'undefined') {
      (window as any).yahooMatchupResponse = response.data;
    }
    
    // Check structure
    const structure = {
      hasFantasyContent: !!response.data?.fantasy_content,
      hasLeague: !!response.data?.fantasy_content?.league,
      leagueIsArray: Array.isArray(response.data?.fantasy_content?.league),
      hasScoreboard: !!response.data?.fantasy_content?.league?.[1]?.scoreboard,
      hasMatchups: !!response.data?.fantasy_content?.league?.[1]?.scoreboard?.matchups
    };
    
    try {
      // Find scoreboard data in response
      let scoreboardData: any;
      if (response.data.fantasy_content?.league) {
        const league = response.data.fantasy_content.league;
        if (Array.isArray(league)) {
          // Look for scoreboard in array elements
          scoreboardData = league[1]?.scoreboard || 
                          league.find(item => item?.scoreboard)?.scoreboard;
        } else if (typeof league === 'object') {
          scoreboardData = league.scoreboard || 
                          league['1']?.scoreboard ||
                          league['0']?.scoreboard;
        }
      }
      
      if (scoreboardData) {
        
        // Scoreboard has matchups in numbered keys (0, 1, 2, etc)
        // Not in a 'matchups' property
        const week = scoreboardData.week || 1;
        
        Object.keys(scoreboardData).forEach(key => {
          // Skip 'week' and other non-matchup keys
          if (key !== 'week' && scoreboardData[key]) {
            
            // Yahoo uses "matchups" (plural) not "matchup"
            const matchupsWrapper = scoreboardData[key].matchups;
            
            if (matchupsWrapper) {
              
              // Iterate through each matchup in matchups
              Object.keys(matchupsWrapper).forEach(mKey => {
                if (mKey !== 'count' && matchupsWrapper[mKey]) {
                  
                  const matchupItem = matchupsWrapper[mKey];
                  let matchupData = matchupItem.matchup ? 
                                   (Array.isArray(matchupItem.matchup) ? matchupItem.matchup[0] : matchupItem.matchup) :
                                   matchupItem;
                  
                  // If matchupData is an array, teams might be in the second element
                  if (Array.isArray(matchupData)) {
                    // Look for teams in array elements
                    const teamsElement = matchupData.find(elem => elem?.teams) || matchupData[0];
                    if (teamsElement?.teams) {
                      matchupData = teamsElement;
                    }
                  }
                  
                  // Yahoo might return teams in different formats
                  let teamsData: any = null;
                  if (matchupData?.teams) {
                    teamsData = matchupData.teams;
                  } else if (matchupData?.['0']?.teams) {
                    teamsData = matchupData['0'].teams;
                  } else if (matchupData && typeof matchupData === 'object') {
                    // Look for teams in any property
                    const values = Object.values(matchupData);
                    for (const value of values) {
                      if (value && typeof value === 'object' && 'teams' in value) {
                        teamsData = (value as any).teams;
                        break;
                      }
                    }
                  }
                  
                  if (teamsData) {
                    const teams: any[] = [];
                    
                    // Get week from matchup data
                    const matchupWeek = matchupData.week || week || 1;
                    
                    // Parse teams in matchup
                    Object.keys(teamsData).forEach(tKey => {
                      if (tKey !== 'count' && teamsData[tKey]?.team) {
                        const teamWrapper = teamsData[tKey].team;
                        const teamArray = Array.isArray(teamWrapper) ? teamWrapper[0] : teamWrapper;
                        
                        // Extract team info from array structure
                        let teamId = tKey;
                        let teamName = 'Unknown Team';
                        let points = '0';
                        
                        if (Array.isArray(teamArray)) {
                          // Team data is an array of objects
                          for (let i = 0; i < teamArray.length; i++) {
                            if (teamArray[i] && typeof teamArray[i] === 'object') {
                              if (teamArray[i].team_key) teamId = teamArray[i].team_key;
                              if (teamArray[i].team_id) teamId = teamArray[i].team_id;
                              if (teamArray[i].name) teamName = teamArray[i].name;
                              if (teamArray[i].team_points?.total) points = teamArray[i].team_points.total;
                              if (teamArray[i].points?.total) points = teamArray[i].points.total;
                            }
                          }
                        } else if (teamArray && typeof teamArray === 'object') {
                          teamId = teamArray.team_id || teamArray.team_key || tKey;
                          teamName = teamArray.name || 'Unknown Team';
                          points = teamArray.team_points?.total || teamArray.points?.total || '0';
                        }
                        
                        teams.push({
                          id: teamId,
                          name: teamName,
                          score: parseFloat(points)
                        });
                      }
                    });
                    
                    if (teams.length >= 2) {
                      matchups.push({
                        week: matchupWeek,
                        team1: {
                          userId: teams[0].id,
                          teamName: teams[0].name,
                          points: teams[0].score,
                          roster: [] // Yahoo doesn't provide roster data in matchup endpoint
                        },
                        team2: {
                          userId: teams[1].id,
                          teamName: teams[1].name,
                          points: teams[1].score,
                          roster: [] // Yahoo doesn't provide roster data in matchup endpoint
                        }
                      });
                    }
                  }
                }
              });
            }
          }
        });
      }
    } catch (error) {
      // Silently handle error
    }
    
    // If no matchups, return a message indicating why
    if (matchups.length === 0) {
      // Silently handle no matchups
    }
    
    return {
      success: true,
      data: matchups,
      platform: 'yahoo'
    };
  }

  // Utility method - limited due to OAuth complexity
  async getLeagueData(leagueKey: string, week?: number) {
    const currentWeek = week || 1;
    
    const [league, rosters] = await Promise.all([
      this.getLeague(leagueKey),
      this.getLeagueRosters(leagueKey, currentWeek)
    ]);

    return {
      league,
      rosters,
      matchups: { 
        success: false, 
        error: 'Yahoo matchups require additional implementation', 
        platform: 'yahoo' as const 
      }
    };
  }

  // OAuth helper methods (would need full OAuth implementation)
  getOAuthUrl(clientId: string, redirectUri: string): string {
    const params = new URLSearchParams({
      client_id: clientId,
      redirect_uri: redirectUri,
      response_type: 'code',
      scope: 'fspt-r' // Yahoo Fantasy Sports read permission
    });
    
    return `https://api.login.yahoo.com/oauth2/request_auth?${params.toString()}`;
  }
}

export const yahooApi = new YahooAPI();